import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import imgError403 from 'assets/images/errors/error403.png';
import imgError404 from 'assets/images/errors/error404.png';
import imgError500 from 'assets/images/errors/error500.png';
import imgError503 from 'assets/images/errors/error503.png';
import Button from 'components/atoms/Button';
import Heading from 'components/atoms/Heading';
import Image from 'components/atoms/Image';
import Link from 'components/atoms/Link';
import Text from 'components/atoms/Text';
import { useAppDispatch } from 'store/hooks';
import { toggleLayout } from 'store/layout';
import mapModifiers from 'utils/functions';

interface ErrorsProps {
  handleContact?: () => void;
  statusCode?: 403 | 404 | 500 | 503 | 'stockSymbol404';
  titlePage?: string;
  desc?: string;
  href?: string;
  target?: string;
  btnText?: string;
}

const Errors: React.FC<ErrorsProps> = ({
  handleContact,
  statusCode,
  titlePage,
  desc,
  href = '/lien-he',
  target,
  btnText = 'LIÊN HỆ',
}) => {
  const { goBack } = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(toggleLayout('main-layout'));
  }, [dispatch]);

  const { title, description, imgSrc } = useMemo(() => {
    switch (statusCode) {
      case 403:
        return {
          imgSrc: imgError403,
          title: titlePage || 'Rất tiếc, chúng tôi đang phát triển tính năng này',
          description:
            desc || 'Vui lòng trở về trang chủ hoặc liên hệ với chúng tôi để được hỗ trợ.',
        };
      case 500:
        return {
          imgSrc: imgError500,
          title: titlePage || 'Lỗi máy chủ nội bộ',
          description:
            desc || 'Rất tiếc, một vài lỗi không mong muốn đã xảy ra, </br> chúng tôi đang cố gắng khắc phục sự cố. Quý Khách xin vui lòng thử lại sau.',
        };
      case 503:
        return {
          imgSrc: imgError503,
          title: 'Rất tiếc, hệ thống đang được bảo trì',
          description: 'Trang thông tin Quý Khách tìm kiếm đang trong quá trình bảo trì, </br> vui lòng thử lại sau.',
        };
      case 'stockSymbol404':
        return {
          imgSrc: imgError404,
          title: titlePage || 'Rất tiếc, chúng tôi không tìm thấy mã chứng khoán này',
          description:
              desc || 'Vui lòng trở về trang chủ hoặc liên hệ với chúng tôi để được hỗ trợ.',
        };

      default:
        return {
          imgSrc: imgError404,
          title: titlePage || 'Rất tiếc, chúng tôi không tìm thấy trang này',
          description:
            desc || 'Vui lòng trở về trang chủ hoặc liên hệ với chúng tôi để được hỗ trợ.',
        };
    }
  }, [desc, statusCode, titlePage]);

  return (
    <div className={mapModifiers('t-errors', statusCode === 503 ? 'maintenance' : '')}>
      <div className="t-errors_image">
        <Image alt="Error" size="cover" ratio="360x316" imgSrc={imgSrc} />
      </div>
      <div className="t-errors_title">
        <Heading
          type="h5"
          modifiers={['700', 'letterSpacing', 'center', 'britishRacingGreen']}
        >
          {title}
        </Heading>
      </div>
      <div className="t-errors_subTitle">
        <Text
          modifiers={[
            '400',
            'letterSpacing',
            'center',
            'britishRacingGreen',
            'xl',
          ]}
          content={description}
        />
      </div>
      {
        statusCode !== 503 && (
        <div className="t-errors_button">
          <div className="t-errors_back">
            <Button
              modifiers={['700', 'center', 'uppercase', 'primary']}
              handleClick={() => goBack()}
            >
              {t('additional.come_back')}
            </Button>
          </div>
          <div className="t-errors_content">
            <Link
              href={href}
              target={target}
              handleClick={() => {
                if (handleContact) {
                  handleContact();
                }
              }}
            >
              <Button modifiers={['700', 'center', 'uppercase', 'outline']}>
                {btnText}
              </Button>
            </Link>
          </div>
        </div>
        )
      }
    </div>
  );
};

Errors.defaultProps = {
  statusCode: undefined,
  handleContact: undefined,
  titlePage: undefined,
  desc: undefined,
  href: undefined,
  target: '_self',
  btnText: undefined,
};

export default Errors;
